import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { ApiFactory } from '../api/easypos-api';
import { IShopDay, ShopDayState } from '../api/model/requests';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { styles as sharedStyles } from '../styles/shared-styles';
import { designPrimitives } from '../styles/design-primitives';
import { resolveRouterPath, router } from '../router';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Z4 — the recent-days picker that feeds the daily Z-report. Lists recent
// trading days newest-first (including the current open/closing day); each row
// opens that day's Z-report.
@customElement('shop-days-page')
export class ShopDaysPage extends Base {
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _days: IShopDay[] = [];

  static styles = [
    sharedStyles,
    designPrimitives,
    css`
      :host {
        display: block;
        max-width: 900px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .day-card {
        margin-bottom: 10px;
        padding: 14px 16px;
        display: block;
        width: 100%;
        box-sizing: border-box;
        text-align: left;
        font: inherit;
        background: var(--s-surface, #fff);
        border: 1px solid var(--s-line, rgba(15, 30, 55, 0.08));
        border-radius: 16px;
        box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.05));
        cursor: pointer;
        transition:
          transform 140ms ease,
          box-shadow 140ms ease;
      }
      .day-card:hover {
        transform: translateY(-1px);
        box-shadow: var(--app-shadow-md, 0 6px 18px rgba(15, 30, 55, 0.08));
      }
      .day-card:focus-visible {
        outline: none;
        box-shadow: var(--sl-focus-ring, 0 0 0 3px rgba(15, 30, 55, 0.25));
      }
      .day-row {
        display: flex;
        align-items: center;
        gap: 14px;
      }
      .day-row .body {
        flex: 1 1 auto;
        min-width: 0;
      }
      .day-row .title {
        font-family: var(--font-display, inherit);
        font-weight: 700;
        font-size: 1rem;
        color: var(--s-ink, currentColor);
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .day-row .sub {
        font-size: 0.8rem;
        color: var(--s-ink-soft, #666);
        margin-top: 3px;
        display: flex;
        gap: 4px 14px;
        flex-wrap: wrap;
      }
      .empty {
        padding: 32px 24px;
        background: var(--s-canvas-soft, #f7f7f7);
        border-radius: 16px;
        color: var(--s-ink-soft, #666);
        text-align: center;
      }
    `,
  ];

  private _loadedShopName: string | null = null;

  override updated(changed: Map<string | number | symbol, unknown>): void {
    super.updated(changed);
    const shop = this.activeShop?.shopName ?? null;
    if (shop && shop !== this._loadedShopName) {
      this._loadedShopName = shop;
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    if (!this.activeShop) return;
    this._loading = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.listDays(this.activeShop.shopName, 30);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not load trading days.';
        return;
      }
      this._days = r.data;
    } finally {
      this._loading = false;
    }
  }

  private _open(dayId: string): void {
    router.navigate(resolveRouterPath(`shop/days/${dayId}/z-report`));
  }

  private _badge(state: ShopDayState) {
    switch (state) {
      case 'open':
      case 'opening':
        return html`<sl-badge variant="success" pill>Open</sl-badge>`;
      case 'closing':
        return html`<sl-badge variant="warning" pill>Closing</sl-badge>`;
      default:
        return html`<sl-badge variant="neutral" pill>Closed</sl-badge>`;
    }
  }

  private _renderDay(d: IShopDay) {
    const opened = new Date(d.openedUtc);
    const closed = d.closedUtc ? new Date(d.closedUtc) : null;
    return html`
      <button class="day-card" @click=${() => this._open(d.id)}>
        <div class="day-row">
          <span class="icon-tile">
            <sl-icon name="calendar-check"></sl-icon>
          </span>
          <div class="body">
            <div class="title">${d.displayLabel} ${this._badge(d.state)}</div>
            <div class="sub">
              <span>Opened ${opened.toLocaleString()}</span>
              ${closed ? html`<span>Closed ${closed.toLocaleString()}</span>` : nothing}
            </div>
          </div>
          <sl-icon class="list-row__chevron" name="chevron-right"></sl-icon>
        </div>
      </button>
    `;
  }

  render() {
    if (!this.activeShop) return html`<p>No active shop.</p>`;
    return html`
      <div class="page-head">
        <h1 class="page-head__title">Daily Z-reports</h1>
        <p class="page-head__sub">
          Recent trading days. Open one to see the day's sales, refunds, and
          net.
        </p>
      </div>

      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._loading
          ? html`<p>Loading...</p>`
          : this._days.length === 0
            ? html`<div class="empty">
                No trading days yet. Open a day to start trading.
              </div>`
            : this._days.map((d) => this._renderDay(d))
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'shop-days-page': ShopDaysPage;
  }
}
